import React, { useState } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { Form, Tooltip } from "antd";

import FileUpload from "../Forms/FileUpload";
import ImageGallery from "../../ImageGallery";
import { AtticusDivider } from "../Divider";
import { InfoIcon, UploadIcon } from "../../../content/icons";
import { Modal } from "../Modal";
import { ButtonType, ButtonSize, Button, DeleteImageButton } from "../Buttons";
import { AtticusTextField } from "../Form";
import { FormItemProps } from "antd/es/form/FormItem";
import { sanitizeAltText } from "../../../utils/helper";

interface ImageUploadComponentProps {
  handler: (url: string, alternativeText: string) => void;
  show: boolean;
}

const fullWidthFormItemProps: FormItemProps = {
  colon: false,
  labelCol: { span: 24 },
  style: { marginBottom: 15 },
};

const ImageUploadComponent = ({ handler, show }: ImageUploadComponentProps) => {
  const [uploading, setUploading] = useState(false);
  const [imageURL, setImageURL] = useState<string>("");
  const [alternativeText, setAlternativeText] = useState<string>("");

  const tooltipText = (
    <span>
      If this image is purely decorative or explained within the written
      content, leave the alt text blank. If the image adds important information
      to your work, include alt text to describe it clearly in accordance with
      accessibility guidelines. Background images are considered decorative and
      should not include alt text.{" "}
      <a
        href="https://www.atticus.io/accessible-ebooks-how-to-write-good-alt-text/"
        target="_blank"
        rel="noreferrer"
      >
        Learn more about alt text best practices.
      </a>
    </span>
  );

  const clearForm = () => {
    setImageURL("");
  };

  const clearModal = () => {
    clearForm();
    handler("", "");
  };

  const onSubmit = async () => {
    if (imageURL) {
      handler(imageURL, alternativeText);
    }
  };

  const getIGURL = (link) => {
    setImageURL(link);
  };

  return (
    <Modal
      open={show}
      title="Add an image"
      onCancel={() => clearModal()}
      centered={true}
      bodyStyle={{
        paddingLeft: 0,
        paddingRight: 0,
      }}
      wrapClassName="image-toolbar-modal-wrap"
    >
      <div className="image-toolbar-body-wrapper">
        <Form className="image-toolbar-ant-form">
          <>
            <ImageGallery
              handleImageUrl={getIGURL}
              ImageGalleryButtonText="Select from image gallery"
              renderButtonProps={{
                type: "at-primary",
                backgroundColor: "green",
                size: ButtonSize.MEDIUM,
                block: true
              }}
            />
            <AtticusDivider>
              <p className="image-toolbar-divider-text">Or</p>
            </AtticusDivider>
            <h4 className="image-toolbar-upload-header">Upload a new image</h4>
            {imageURL ? (
              <>
                <div
                  className="image-toolbar-image-previewer contain"
                  style={{ backgroundImage: `url(${imageURL})` }}
                >
                  <div className="image-toolbar-image-previewer-container">
                    <DeleteImageButton onClick={() => clearForm()}/>
                  </div>
                </div>
                <Form.Item
                  label={
                    <span className="edit-image-form-label">
                      Alt text{" "}
                      <Tooltip placement="right" title={tooltipText}>
                        <div className="info-icon-wrapper">
                          <InfoIcon />
                        </div>
                      </Tooltip>
                    </span>
                  }
                  rules={[{ required: false }]}
                  {...fullWidthFormItemProps}
                >
                  <AtticusTextField
                    value={alternativeText}
                    onChange={(e) => {
                      const sanitizedValue = sanitizeAltText(e.target.value);
                      setAlternativeText(sanitizedValue);
                    }}
                    placeholder="Alt text"
                    inputType="secondary"
                    maxLength={140}
                  />
                </Form.Item>
                <Button
                  type={ButtonType.PRIMARY}
                  size={ButtonSize.MEDIUM}
                  backgroundColor="green"
                  onMouseDown={() => onSubmit()}
                  fullWidth
                >
                  Set Image
                </Button>
              </>
            ) : (
              <>
                <Form.Item
                  name="upload"
                  className="image-toolbar-upload-form-item"
                >
                  <FileUpload
                    fileType="image/png, image/jpg, image/jpeg"
                    onFileUploadStart={() => setUploading(true)}
                    onFileUpload={(fileUrl) => {
                      if (fileUrl) setImageURL(fileUrl);
                    }}
                    onFileUploadEnd={() => setUploading(false)}
                    folder="images"
                    styleOverrides={{
                      backgroundColor: "#E7EDF8",
                      borderRadius: 4,
                      borderWidth: 2,
                      borderStyle: "dashed",
                      borderColor: "#ADC1E3",
                    }}
                  >
                    <div className="image-toolbar-dnd-container">
                      {uploading ? (
                        <span className="image-toolbar-dnd-text-highlight">
                          <LoadingOutlined /> Uploading
                        </span>
                      ) : (
                        <>
                          <span>
                            <UploadIcon color="#3363B9" />
                          </span>
                          <p>
                            <span className="image-toolbar-dnd-text-highlight">
                              Click to upload image
                            </span>{" "}
                            or drag and drop
                          </p>
                        </>
                      )}
                    </div>
                  </FileUpload>
                </Form.Item>
                <div className="image-toolbar-upload-footer">
                  <p className="image-toolbar-upload-footer-text">
                    Supported formats: PNG, JPG
                  </p>
                  <p className="image-toolbar-upload-footer-text">
                    Maximum size: 10mb
                  </p>
                </div>
              </>
            )}
          </>
        </Form>
      </div>
    </Modal>
  );
};

export default ImageUploadComponent;
